"use client";

import type { Espece } from "@repo/types";
import { ImageIcon, ZoomIn } from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { imageService } from "../../../lib/services";
import { EspeceImagePreviewDialog } from "./espece-image-preview-dialog";

interface EspeceTableImageCellProps {
  espece: Espece;
}

function buildEspeceLabel(espece: Espece): string {
  const parts = [espece.genre, espece.name?.toLowerCase()].filter(Boolean);
  return parts.join(" ") || "Espèce";
}

function buildEspeceSubtitle(espece: Espece): string | undefined {
  const parts = [espece.nom_fr, espece.cultivar, espece.categorie].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

export function EspeceTableImageCell({ espece }: EspeceTableImageCellProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const title = buildEspeceLabel(espece);
  const subtitle = buildEspeceSubtitle(espece);
  const alt = espece.nom_fr || espece.name || "Espèce";

  if (!espece.img_url) {
    return (
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/30"
        aria-label="Aucune image"
      >
        <ImageIcon className="h-4 w-4 text-muted-foreground/70" aria-hidden />
      </div>
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="group relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-border/80 bg-muted/20 transition-all duration-200 hover:border-primary/50 hover:shadow-md hover:ring-2 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label={`Aperçu de ${title}`}
          >
            <img
              src={imageService.getOptimizedImageUrl(espece.img_url, "espece", 80, 80)}
              alt={alt}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/25 group-hover:opacity-100">
              <ZoomIn className="h-4 w-4 text-white drop-shadow-md" aria-hidden />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Cliquer pour agrandir</TooltipContent>
      </Tooltip>

      <EspeceImagePreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        imgUrl={espece.img_url}
        title={title}
        subtitle={subtitle}
      />
    </>
  );
}
